<template>
  <div id="person" class="entity">
    <div id="personHeaderWrapper" class="hide-when-embedded padding-bottom-2">
      <person-header :entity="entity" />
    </div>
    <div class="hide-when-embedded content-box content-box-padding">
      <div class="container-fluid container-fluid-responsive">
        <div class="row gutter-responsive">
          <div class="col-12 col-md-7">
            <div v-if="showResume">
              <expandable-resume v-if="entity.resume" :resume="entity.resume" :expandable="entity.relationCount > 5"></expandable-resume>
              <div v-else class="loader d-flex">
                <Loader :size="40" class="mr-3" />
                <span>Henter erhvervsresume..</span>
              </div>
              <help-box v-if="entity.resume"
                :title="'HVOR KOMMER DENNE TEKST FRA'"
                :paragraph="'Lassos robot skriver automatisk resumeer af personers historik i dansk erhvervsliv. Al data hentes automatisk og realtime fra CVR og sammenskrives i læsbare tekster, der giver et overblik over personens tidligere og nuværende relationer til danske virksomheder – herunder bestyrelses- og direktionsposter, ejerhierarki, konkursrelationer (firmaer, hvor man har haft en rolle op til 12 måneder før det blev meldt under konkurs) og andet relevant.'"
              />
              <div class="d-block d-md-none margin-top-2 padding-bottom-1"></div>
            </div>
            <help-box
              v-else-if="windowSize.width > windowSize.sizes.sm"
              :expanded="true"
              :title="'DENNE PERSON HAR INTET ERHVERVSRESUME'"
              :paragraph="'Lassos robot skriver resumeer af personers historik i dansk erhvervsliv, hvis personen har nok registreringer i CVR. Hvis der kun er få registreringer og disse primært er i personligt ejede virksomheder, vil der ikke være et resume om denne.'"
            />
          </div>

          <div id="personContentSidebarWrapper" class="col-12 col-md-5">
            <!-- <div class="d-block d-md-none margin-top-2 padding-bottom-1"></div> -->
            <expandable-list v-if="entity.network && entity.network.total" title="PERSONER I NETVÆRK" :open="true" class="compact">
              <div v-if="entity.network">
                <div v-for="(person, index) in entity.network.topList.slice(0, 3)" class="list-element" :key="index">
                  <router-link :to="{ name: 'person', params: { id: person.unitNo }}" class="plausible-event-name=person+network+click">{{person.name}}</router-link>
                </div>
                <div v-show="entity.network.total > 3" class="list-element text-semimuted">
                  .. og {{entity.network.total - 3 | pluralize('andre', 'anden', 'andre')}}
                </div>
                <LassoxLink
                  trackingEventName="person+cvrnetwork+link+click"
                  path="cvrnetwork"
                  class="lassox-link"
                  :dialogOptions="{
                    lassoxIntro: {
                      title: 'Se fuldt netværk',
                      text: 'Opret en gratis bruger nu og få større indsigt i netværket – bl.a. tætteste relationer og samlet antal.',
                      image: require('@/assets/img/signup-dialog-network.png'),
                      color: 'peach'
                    }
                  }"
                >
                  <Icon icon="share_outline_rounded" size="small" />
                  Se fuldt netværk
                </LassoxLink>
              </div>
            </expandable-list>
            <expandable-list v-if="ownerships.length" :open="true" title="DIREKTE EJERSKABER" class="compact">
              <div v-for="(ownership, index) in ownerships.slice(0, 5)" :key="index" class="list-element">
                <router-link :to="{ name: 'company', params: { id: ownership.company.cvrNumber, name: $store.state.names['CVR-1-' + ownership.company.cvrNumber] }}" class="plausible-event-name=person+direct+ownership+click">{{ownership.company.newestName.name}}</router-link>
              </div>
              <div v-show="ownerships.length > 5" class="list-element text-semimuted">
                .. og {{ownerships.length - 5 | pluralize('andre', 'anden', 'andre')}}
              </div>
            </expandable-list>
            <expandable-list :open="true" title="AKTIVE ERHVERVSROLLER" class="compact">
              <div v-for="(relation, index) in entity.activeRelations.slice(0, 5)" class="list-element" :key="index">
                <router-link :to="{ name: 'company', params: { id: relation.company.cvrNumber, name: $store.state.names['CVR-1-' + relation.company.cvrNumber] }}" class="plausible-event-name=person+active+business+roles+click">{{relation.company.newestName.name}}</router-link>
                <div class="text-semimuted relation-info">{{ relation.activeRoles | join(', ') }}</div>
              </div>
              <span v-show="entity.activeRelations.length === 0 && entity.person.company" class="text-semimuted">Ingen aktive erhvervsroller, men {{entity.person.firstName}} har tidligere være tilknyttet firmaet {{entity.person.company.name}}</span>
              <span v-show="entity.activeRelations.length === 0 && !entity.person.company" class="text-semimuted">{{entity.person.firstName}} har ingen aktive erhvervsroller</span>
              <div v-show="entity.activeRelations.length > 5" class="list-element text-semimuted">
                .. og {{entity.activeRelations.length - 5 | pluralize('andre', 'anden', 'andre')}}
              </div>
              <LassoxLink
                trackingEventName="person+history+link+click"
                path="history"
                class="lassox-link"
                :dialogOptions="{ lassoxIntro: {
                      title: 'Se personens historik',
                      text: 'Opret en gratis bruger nu og dyk ned i personens fortid. Se bl.a. tidligere firmaer, bestyrelsesposter og ejerforhold.',
                      image: require('@/assets/img/signup-dialog-history.png'),
                      color: 'fuchsia'
                    }
                  }"
                >
                <Icon icon="history" size="small" />
                Se historik
              </LassoxLink>
            </expandable-list>
            <expandable-list v-if="entity.bankruptcies.length > 0" title="KONKURSRELATIONER" :open="true" class="compact">
              <div v-for="(company, index) in entity.bankruptcies.slice(0, 5)" class="list-element" :key="index">
                <router-link :to="{ name: 'company', params: { id: company.id, name: $store.state.names['CVR-1-' + company.id] }}" @click.native="$root.trackEvent({eventCategory:'Click', eventAction: 'Konkursrelation', eventLabel: $route.fullPath})">{{company.name}}</router-link>
                <div v-show="company.recentName && company.recentName !== company.name" class="text-semimuted relation-info">Tidl. {{ company.recentName }}</div>
              </div>
              <div v-show="entity.bankruptcies.length > 5" class="list-element text-semimuted">
                .. og {{entity.bankruptcies.length - 5 | pluralize('andre', 'anden', 'andre')}}
              </div>
            </expandable-list>
            <!--<expandable-list title="DEL MED ANDRE" :open="true" :disabled="true" :no-icon="true" class="compact">
              <social-buttons class="margin-top-1"></social-buttons>
            </expandable-list>-->
          </div>
        </div>
      </div>
    </div>

    <!-- <Webinar style="margin-top: 30px" /> -->
    <!-- <ebook style="margin-top: 30px" /> -->

    <div class="news-feed-header">
      <news-feed v-if="entity.news" :name="name" :news="entity.news" :loading="loadingNews" feed-type="entity-related" :is-query-related="false"></news-feed>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import Loader from '@/components/Loader'
import { store } from '@/assets/js/store.js'
import { EventBus, Story } from '@/assets/js/helpers.js'
import Directives from '@/assets/js/directives.js'
import PersonHeader from '@/components/PersonHeader'
import ExpandableList from '@/components/ExpandableList'
import ExpandableResume from '@/components/ExpandableResume'
// import SocialButtons from '@/components/SocialButtons'
import NewsFeed from '@/components/NewsFeed'
import HelpBox from '@/components/HelpBox'
import Webinar from '@/components/WebinarNew'
import Ebook from '@/components/Ebook'
import userStore from '@/modules/users/store'
import windowSize from '@/window-size'
import LassoxLink from '@/components/LassoxLink.vue'
import Icon from 'vue-lasso-core/components/Icon.vue'

export default {
  name: 'person',
  data () {
    return {
      windowSize,
      loadingNetwork: false,
      loadingNews: false
    }
  },
  computed: {
    entity: function () {
      return this.$store.state.entities[this.$route.meta.lassoIdPrefixes + this.$route.params.id]
    },
    name: function () {
      return this.entity.person.firstName + ' ' + this.entity.person.lastName
    },
    intials: function () {
      var allInitials = this.name.split(' ').map(s => (s.substring(0, 1))).join('').toUpperCase()
      if (allInitials.length > 3) return allInitials.slice(0, 1) + allInitials.slice(2)
      else return allInitials
    },
    address: function () {
      return this.entity.person.streetName
      ? `${this.entity.person.address1}, ${this.entity.person.postalCode} ${this.entity.person.city}, ${this.entity.person.country}`
      : `${this.entity.person.address1.replace(/(?:\s+\r\n|\s+\r|\s+\n)/g, ', ')}, ${this.entity.person.country}`
    },
    ownerships: function () {
      return this.entity.activeRelations.filter(r => r.isCurrentOwner)
    },
    networkCount: function () {
      return this.entity.network ? this.entity.network.total : '-'
    },
    startedBefore1980: function () {
      return this.entity.startDate ? new Date(this.entity.startDate).getFullYear() < 1980 : true
    },
    profileComponent: function () {
      return { template: '<span>' + Vue.filter('linkify')(this.entity.profile, false) + '</span>' }
    },
    showResume () {
      if (this.entity.lassoId === 'CVR-3-4000567679') return false // to be deleted when we fix the issue that the guy was complaining about..
      else return (this.entity && this.entity.relatedToCoorporation && this.entity.relationCount > 3 && !this.entity.isOtherParticipant)
    }
  },
  methods: {
    loadNews: function (more) {
      if (this.loadingNews || (this.entity.news && (!more || this.entity.news.endReached))) return
      else this.loadingNews = true
      var nextPage = this.entity.news && more ? this.entity.news.page + 1 : 1
      this.$store.dispatch('loadEntityNews', { lassoId: this.entity.lassoId, page: nextPage, count: 12 }).then(() => { this.loadingNews = false })
    }
  },
  beforeRouteEnter: (to, from, next) => {
    store.dispatch('loadEntity', { lassoId: to.meta.lassoIdPrefixes + to.params.id, includeNews: true }).then((entity) => {
      store.dispatch('loadNetwork', to.meta.lassoIdPrefixes + to.params.id)

      if (entity && entity.relatedToCoorporation && entity.relationCount > 3 && !entity.isOtherParticipant) {
        store.dispatch('loadResume', { lassoId: to.meta.lassoIdPrefixes + to.params.id, short: false })
      }

      if (to.query.unfollow == 'true') {
        const unfollow = () => userStore.user.unfollowEntities([{
          lassoId: entity.lassoId,
          name: entity.name,
        }])

        if (!userStore.user) userStore.showLoginDialog({ onLoggedIn: unfollow })
        else unfollow()
      }

      next()
    })
  },
  beforeRouteUpdate: function (to, from, next) {
    store.dispatch('loadEntity', { lassoId: to.meta.lassoIdPrefixes + to.params.id, includeNews: true }).then((entity) => {
      store.dispatch('loadNetwork', to.meta.lassoIdPrefixes + to.params.id)
      if (entity && entity.relatedToCoorporation && entity.relationCount > 3 && !entity.isOtherParticipant) store.dispatch('loadResume', { lassoId: to.meta.lassoIdPrefixes + to.params.id, short: false })
      next()
    })
  },
  mounted: function () {
    var that = this
    EventBus.$on('loadNews', () => this.loadNews(true))
    EventBus.$on('loadLongResume', () => this.$store.dispatch('loadResume', { lassoId: that.entity.lassoId, short: false }))
  },
  destroyed: function () {
    EventBus.$off('loadNews')
  },
  components: {
    PersonHeader,
    ExpandableList,
    NewsFeed,
    ExpandableResume,
    HelpBox,
    Loader,
    Webinar,
    Ebook,
    LassoxLink,
    Icon,
  },
  directives: {
    'event-on-visible': Directives.eventOnVisible
  },
}
</script>

<style lang="less">
  #person {

    #personHeaderWrapper {
      padding-top: 50px;
    }
    .expandable-resume {
      margin-bottom: 30px;
    }
    .contact-info {
      font-weight: 500;
      font-size: 16px;
      padding-bottom: 15px;
    }

    .expandable-list {
      &:not(:last-child) {
        margin-bottom: 30px;
      }
    }

    .relation-info {
      margin-top: 3px;
      font-size: 13px;
    }

    .lassox-link {
      display: flex;
      align-items: center;
      margin-top: 16px;
      gap: 8px;
      color: #FF6B7E;
      .md-icon {
        color:#FF6B7E
      }
    }

    .loader {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 40px;
      span {
        font-size: 16px;
        color: rgba(97,97,97,.7);
      }
    }
  }

  #app.xxs  {
    #person {
      #personHeaderWrapper {
        padding-top: 30px;
      }
    }
  }

  #app.xs {
    #person {
      #personHeaderWrapper {
          padding-top: 20px;
      }
    }
  }

  #app.sm, #app.md  {
    #person {
      #personHeaderWrapper {
          padding-top: 50px;
      }
    }
  }

</style>
